import { publicPath } from '../publicPath';
import {
  defaultAssetsBrowseParams,
  GUEST_ALLOWED_VIEWS,
  type AppView,
  type AssetsBrowseParams,
  type AuthPane,
} from './AppNavigationContext';
import {
  assistantPriceConstraintsEqual,
  assistantPriceToUrl,
  parseAssistantPriceFromUrl,
} from '../assistant/assistantPriceConstraint';
import { isAssetBrowseListSortMode } from '../utils/assetBrowseListSort';

export type AppHistorySnapshot = {
  view: AppView;
  authPane: AuthPane;
  assetsBrowseParams: AssetsBrowseParams;
  /** Keyword on the search tab; kept when opening asset detail so back restores results. */
  searchQuery: string | null;
};

const VIEW_PATH: Record<AppView, string> = {
  home: '/home',
  auth: '/auth',
  search: '/search',
  /** `/browse` — not `/assets` (nginx proxies that path to the catalog API). */
  assets: '/browse',
  bookings: '/bookings',
  favorites: '/favorites',
  messages: '/messages',
  /** `/account` — not `/profile` (nginx proxies that path to the profile API). */
  profile: '/account',
  connectionTest: '/connection-test',
};

const PATH_VIEW: Record<string, AppView> = {
  '/': 'home',
  '/home': 'home',
  '/auth': 'auth',
  '/auth/register': 'auth',
  '/auth/login/business-owner': 'auth',
  '/auth/login/business': 'auth',
  '/auth/login/owner': 'auth',
  '/auth/login/asset-owner': 'auth',
  '/auth/register/asset-owner': 'auth',
  '/auth/contact': 'auth',
  '/auth/forgot-password': 'auth',
  '/auth/apple/callback': 'auth',
  '/search': 'search',
  '/browse': 'assets',
  '/assets': 'assets',
  '/bookings': 'bookings',
  '/payment/success': 'bookings',
  '/payment/failure': 'bookings',
  '/payment/cancel': 'bookings',
  '/favorites': 'favorites',
  '/messages': 'messages',
  '/account': 'profile',
  '/profile': 'profile',
  '/connection-test': 'connectionTest',
};

function routerBase(): string {
  const raw = process.env.PUBLIC_URL ?? '';
  return raw.replace(/\/$/, '');
}

export function stripRouterBase(pathname: string, routerBaseOverride?: string): string {
  const base = routerBaseOverride ?? routerBase();
  if (!base) {
    return pathname || '/';
  }
  if (pathname === base || pathname === `${base}/`) {
    return '/';
  }
  if (pathname.startsWith(`${base}/`)) {
    return pathname.slice(base.length) || '/';
  }
  return pathname;
}

function isAppView(value: string): value is AppView {
  return Object.prototype.hasOwnProperty.call(VIEW_PATH, value);
}

export function createDefaultSnapshot(isLoggedIn: boolean): AppHistorySnapshot {
  return {
    view: isLoggedIn ? 'home' : 'auth',
    authPane: 'login',
    assetsBrowseParams: { ...defaultAssetsBrowseParams },
    searchQuery: null,
  };
}

export function snapshotToPath(snapshot: AppHistorySnapshot): string {
  let path = VIEW_PATH[snapshot.view];
  if (snapshot.view === 'auth') {
    if (snapshot.authPane === 'register') {
      path = '/auth/register';
    } else if (snapshot.authPane === 'register-asset-owner') {
      path = '/auth/register/asset-owner';
    } else if (snapshot.authPane === 'login-asset-owner') {
      path = '/auth/login/asset-owner';
    } else if (snapshot.authPane === 'login-business-owner') {
      path = '/auth/login/business-owner';
    } else if (snapshot.authPane === 'contact') {
      path = '/auth/contact';
    } else if (snapshot.authPane === 'forgot-password') {
      path = '/auth/forgot-password';
    }
  }
  const params = new URLSearchParams();
  const browse = snapshot.assetsBrowseParams;
  if (snapshot.view === 'search' && snapshot.searchQuery?.trim()) {
    params.set('q', snapshot.searchQuery.trim());
  }
  if (browse.assetTypeId != null && Number.isFinite(browse.assetTypeId)) {
    params.set('type', String(browse.assetTypeId));
  }
  if (typeof browse.assetTypeLabel === 'string' && browse.assetTypeLabel.trim()) {
    params.set('label', browse.assetTypeLabel.trim());
  }
  if (typeof browse.assetId === 'string' && browse.assetId.trim()) {
    params.set('asset', browse.assetId.trim());
  }
  if (browse.returnView) {
    params.set('return', browse.returnView);
  }
  if (browse.catalogBrowse) {
    params.set('catalog', '1');
  }
  if (browse.listSearch?.trim()) {
    params.set('text', browse.listSearch.trim());
  }
  if (browse.countryId != null && Number.isFinite(browse.countryId)) {
    params.set('country', String(browse.countryId));
  }
  if (browse.city?.trim()) {
    params.set('city', browse.city.trim());
  }
  if (browse.whenDate?.trim()) {
    params.set('date', browse.whenDate.trim());
  }
  if (browse.whenTime?.trim()) {
    params.set('time', browse.whenTime.trim());
  }
  if (browse.street?.trim()) {
    params.set('street', browse.street.trim());
  }
  if (browse.officeFamily) {
    params.set('officeFamily', '1');
  }
  if (browse.open24h) {
    params.set('open24h', '1');
  }
  const priceUrl = assistantPriceToUrl(browse.price);
  if (priceUrl) {
    params.set('price', priceUrl);
  }
  const assetIds = (browse.assetIds ?? []).map((id) => id.trim()).filter(Boolean);
  if (assetIds.length > 0) {
    params.set('ids', assetIds.join(','));
  }
  if (browse.listSort) {
    params.set('sort', browse.listSort);
  }
  if (browse.returnView === 'search' && snapshot.searchQuery?.trim()) {
    params.set('q', snapshot.searchQuery.trim());
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export function snapshotToUrl(snapshot: AppHistorySnapshot): string {
  return publicPath(snapshotToPath(snapshot));
}

/**
 * Absolute SPA share link for an asset. Always `/browse?asset=…` — never `/assets/…`
 * (nginx proxies `/assets/` to the catalog API and returns raw 401 JSON without a Bearer token).
 */
export function buildAssetBrowseShareUrl(assetId: string): string {
  const id = assetId.trim();
  if (!id) {
    return typeof window !== 'undefined' ? window.location.href : publicPath('/browse');
  }
  const path = publicPath(`/browse?asset=${encodeURIComponent(id)}`);
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  if (typeof window === 'undefined' || !window.location?.origin) {
    return path;
  }
  return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
}

export function parseLocation(
  pathname: string,
  search: string,
  isLoggedIn: boolean,
): AppHistorySnapshot {
  const rel = stripRouterBase(pathname);
  const normalized =
    rel.length > 1 && rel.endsWith('/') ? rel.slice(0, -1) : rel || '/';

  let view: AppView = PATH_VIEW[normalized] ?? (isLoggedIn ? 'home' : 'auth');
  let authPane: AuthPane = 'login';
  if (normalized === '/auth/register') {
    authPane = 'register';
  } else if (normalized === '/auth/register/asset-owner') {
    authPane = 'register-asset-owner';
  } else if (normalized === '/auth/login/asset-owner') {
    authPane = 'login-asset-owner';
  } else if (
    normalized === '/auth/login/business-owner' ||
    normalized === '/auth/login/business' ||
    normalized === '/auth/login/owner'
  ) {
    authPane = 'login-business-owner';
  } else if (normalized === '/auth/contact') {
    authPane = 'contact';
  } else if (normalized === '/auth/forgot-password') {
    authPane = 'forgot-password';
  }

  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const assetsBrowseParams: AssetsBrowseParams = { ...defaultAssetsBrowseParams };

  const typeRaw = params.get('type');
  if (typeRaw) {
    const n = Number(typeRaw);
    if (Number.isFinite(n)) {
      assetsBrowseParams.assetTypeId = n;
    }
  }
  const label = params.get('label');
  if (label?.trim()) {
    assetsBrowseParams.assetTypeLabel = label.trim();
  }
  const asset = params.get('asset') ?? params.get('assetId');
  if (asset?.trim()) {
    assetsBrowseParams.assetId = asset.trim();
  }
  const returnView = params.get('return');
  if (returnView && isAppView(returnView)) {
    assetsBrowseParams.returnView = returnView;
  }
  if (params.get('catalog') === '1') {
    assetsBrowseParams.catalogBrowse = true;
  }
  const listSearch = params.get('text')?.trim();
  if (listSearch) {
    assetsBrowseParams.listSearch = listSearch;
  }
  const countryRaw = params.get('country');
  if (countryRaw) {
    const n = Number(countryRaw);
    if (Number.isFinite(n)) {
      assetsBrowseParams.countryId = n;
    }
  }
  const city = params.get('city')?.trim();
  if (city) {
    assetsBrowseParams.city = city;
  }
  const whenDate = params.get('date')?.trim();
  if (whenDate && /^\d{4}-\d{2}-\d{2}$/.test(whenDate)) {
    assetsBrowseParams.whenDate = whenDate;
  }
  const whenTime = params.get('time')?.trim();
  if (whenTime && /^\d{2}:\d{2}$/.test(whenTime)) {
    assetsBrowseParams.whenTime = whenTime;
  }
  const street = params.get('street')?.trim();
  if (street) {
    assetsBrowseParams.street = street;
  }
  if (params.get('officeFamily') === '1') {
    assetsBrowseParams.officeFamily = true;
  }
  if (params.get('open24h') === '1') {
    assetsBrowseParams.open24h = true;
  }
  const price = parseAssistantPriceFromUrl(params.get('price'));
  if (price) {
    assetsBrowseParams.price = price;
  }
  const idsRaw = params.get('ids')?.trim();
  if (idsRaw) {
    const ids = idsRaw
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    if (ids.length > 0) {
      assetsBrowseParams.assetIds = ids;
    }
  }
  const listSort = params.get('sort');
  if (isAssetBrowseListSortMode(listSort)) {
    assetsBrowseParams.listSort = listSort;
  }
  const searchQuery = params.get('q')?.trim() || null;

  if (!isLoggedIn && !GUEST_ALLOWED_VIEWS.includes(view)) {
    view = 'auth';
    authPane = 'login';
  }

  return { view, authPane, assetsBrowseParams, searchQuery };
}

export function snapshotsEqual(a: AppHistorySnapshot, b: AppHistorySnapshot): boolean {
  return (
    a.view === b.view &&
    a.authPane === b.authPane &&
    a.searchQuery === b.searchQuery &&
    a.assetsBrowseParams.assetTypeId === b.assetsBrowseParams.assetTypeId &&
    a.assetsBrowseParams.assetTypeLabel === b.assetsBrowseParams.assetTypeLabel &&
    a.assetsBrowseParams.assetId === b.assetsBrowseParams.assetId &&
    a.assetsBrowseParams.returnView === b.assetsBrowseParams.returnView &&
    a.assetsBrowseParams.catalogBrowse === b.assetsBrowseParams.catalogBrowse &&
    (a.assetsBrowseParams.listSearch ?? null) === (b.assetsBrowseParams.listSearch ?? null) &&
    (a.assetsBrowseParams.countryId ?? null) === (b.assetsBrowseParams.countryId ?? null) &&
    (a.assetsBrowseParams.city ?? null) === (b.assetsBrowseParams.city ?? null) &&
    (a.assetsBrowseParams.whenDate ?? null) === (b.assetsBrowseParams.whenDate ?? null) &&
    (a.assetsBrowseParams.whenTime ?? null) === (b.assetsBrowseParams.whenTime ?? null) &&
    (a.assetsBrowseParams.street ?? null) === (b.assetsBrowseParams.street ?? null) &&
    Boolean(a.assetsBrowseParams.officeFamily) === Boolean(b.assetsBrowseParams.officeFamily) &&
    Boolean(a.assetsBrowseParams.open24h) === Boolean(b.assetsBrowseParams.open24h) &&
    assistantPriceConstraintsEqual(a.assetsBrowseParams.price, b.assetsBrowseParams.price) &&
    (a.assetsBrowseParams.assetIds ?? []).join(',') === (b.assetsBrowseParams.assetIds ?? []).join(',') &&
    (a.assetsBrowseParams.listSort ?? null) === (b.assetsBrowseParams.listSort ?? null)
  );
}
